import { useEffect } from 'react';

import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormProvider } from '@/components/react-hook-form';

import { useManageIntegrationDataContext } from '../../context/manageIntegrationData.context';
import { EntityLinkTable } from './components/EntityLinkTable';
import { TrustsBusinessesEmptyState } from './components/TrustsBusinessesEmptyState';
import { EntityLinkForm } from './hooks/useEntityLinkForm';
import { useTrustsBusinessesLinkRows } from './hooks/useLinkPaneRows';

export interface TrustsBusinessesLinkPaneProps {
  form: EntityLinkForm;
  integrationProviderName: string;
}

/**
 * "Link entities": the household's trusts, charitable vehicles and businesses,
 * and the integration data attached to each.
 */
export function TrustsBusinessesLinkPane({
  form,
  integrationProviderName,
}: TrustsBusinessesLinkPaneProps) {
  const { householdId, isSaving } = useManageIntegrationDataContext();
  const { rows, loading, refetch } = useTrustsBusinessesLinkRows();
  const { formMethods, seed } = form;

  useEffect(() => {
    if (loading) return;
    seed(rows);
  }, [loading, rows, seed]);

  if (!loading && rows.length === 0) {
    return (
      <TrustsBusinessesEmptyState
        householdId={householdId}
        onAfterCreate={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <FormProvider {...formMethods}>
      <FormFieldsDisabledProvider isDisabled={isSaving}>
        <EntityLinkTable
          rows={rows}
          householdId={householdId}
          integrationProviderName={integrationProviderName}
          loading={loading}
        />
      </FormFieldsDisabledProvider>
    </FormProvider>
  );
}
